// src/services/firebase/inventory.ts

import {httpsCallable} from "firebase/functions";
import {functions} from "./config";

// Type definitions
export interface InventoryItem {
  id?: string;
  characterId: string;
  name: string;
  type: "weapon" | "armor" | "item" | "magical" | "consumable";
  rarity: "common" | "uncommon" | "rare" | "very_rare" | "legendary";
  weight: number;
  value: number;
  description: string;
  magicalProperties?: string[];
  attunement?: boolean;
  attunedBy?: string;
  quantity: number;
  equipped: boolean;
  equippedSlot?: string;
  source: "session" | "purchase" | "crafting" | "quest";
  sessionAcquired?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface InventoryStatistics {
  totalItems: number;
  totalWeight: number;
  equippedItems: number;
  attunedItems: number;
  maxAttunement: number;
}

export interface InventoryResponse {
  success: boolean;
  items: InventoryItem[];
  statistics: InventoryStatistics;
}

// Cloud Function callables
const addItemToInventory = httpsCallable(functions, "addItemToInventory");
const removeItemFromInventory = httpsCallable(
  functions,
  "removeItemFromInventory"
);
const updateInventoryItem = httpsCallable(functions, "updateInventoryItem");
const getCharacterInventory = httpsCallable(functions, "getCharacterInventory");

export class InventoryService {
  /**
   * Add an item to a character's inventory
   */
  static async addItem(
    characterId: string,
    item: Omit<InventoryItem, "id" | "characterId" | "createdAt" | "updatedAt">
  ): Promise<{success: boolean; itemId: string; message: string}> {
    try {
      const result = await addItemToInventory({characterId, item});
      return result.data as {
        success: boolean;
        itemId: string;
        message: string;
      };
    } catch (error: any) {
      // Error adding item to inventory - rethrow with proper error message
      throw new Error(error.message || "Failed to add item to inventory");
    }
  }

  /**
   * Remove an item from a character's inventory
   */
  static async removeItem(
    itemId: string,
    quantity: number = 1
  ): Promise<{success: boolean; message: string}> {
    try {
      const result = await removeItemFromInventory({itemId, quantity});
      return result.data as {success: boolean; message: string};
    } catch (error: any) {
      // Error removing item from inventory - rethrow with proper error message
      throw new Error(error.message || "Failed to remove item from inventory");
    }
  }

  /**
   * Update an item in a character's inventory
   */
  static async updateItem(
    itemId: string,
    updates: Partial<
      Pick<
        InventoryItem,
        | "equipped"
        | "equippedSlot"
        | "attunedBy"
        | "quantity"
        | "description"
        | "name"
      >
    >
  ): Promise<{success: boolean; message: string}> {
    try {
      const result = await updateInventoryItem({itemId, updates});
      return result.data as {success: boolean; message: string};
    } catch (error: any) {
      // Error updating inventory item - rethrow with proper error message
      throw new Error(error.message || "Failed to update inventory item");
    }
  }

  /**
   * Get a character's complete inventory
   */
  static async getInventory(characterId: string): Promise<InventoryResponse> {
    try {
      const result = await getCharacterInventory({characterId});
      return result.data as InventoryResponse;
    } catch (error: any) {
      // Error getting character inventory - rethrow with proper error message
      throw new Error(error.message || "Failed to get character inventory");
    }
  }

  /**
   * Equip an item
   */
  static async equipItem(itemId: string, slot: string): Promise<void> {
    try {
      await this.updateItem(itemId, {
        equipped: true,
        equippedSlot: slot,
      });
    } catch (error) {
      // Error equipping item - rethrow for handling by caller
      throw error;
    }
  }

  /**
   * Unequip an item
   */
  static async unequipItem(itemId: string): Promise<void> {
    try {
      await this.updateItem(itemId, {
        equipped: false,
        equippedSlot: undefined,
      });
    } catch (error) {
      // Error unequipping item - rethrow for handling by caller
      throw error;
    }
  }

  /**
   * Attune to a magical item
   */
  static async attuneToItem(
    itemId: string,
    characterId: string
  ): Promise<void> {
    try {
      await this.updateItem(itemId, {
        attunedBy: characterId,
      });
    } catch (error) {
      // Error attuning to item - rethrow for handling by caller
      throw error;
    }
  }

  /**
   * Break attunement to a magical item
   */
  static async breakAttunement(itemId: string): Promise<void> {
    try {
      await this.updateItem(itemId, {
        attunedBy: undefined,
      });
    } catch (error) {
      // Error breaking attunement - rethrow for handling by caller
      throw error;
    }
  }

  /**
   * Update item quantity
   */
  static async updateQuantity(itemId: string, quantity: number): Promise<void> {
    try {
      await this.updateItem(itemId, {quantity});
    } catch (error) {
      // Error updating item quantity - rethrow for handling by caller
      throw error;
    }
  }

  /**
   * Create a new item with default values
   */
  static createDefaultItem(
    characterId: string
  ): Omit<InventoryItem, "id" | "createdAt" | "updatedAt"> {
    return {
      characterId,
      name: "",
      type: "item",
      rarity: "common",
      weight: 0,
      value: 0,
      description: "",
      quantity: 1,
      equipped: false,
      source: "session",
    };
  }

  /**
   * Validate item data before submission
   */
  static validateItem(item: Partial<InventoryItem>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!item.name?.trim()) {
      errors.push("Item name is required");
    }

    if (!item.type) {
      errors.push("Item type is required");
    }

    if (!item.rarity) {
      errors.push("Item rarity is required");
    }

    if (item.weight === undefined || item.weight < 0) {
      errors.push("Weight must be a positive number");
    }

    if (item.value === undefined || item.value < 0) {
      errors.push("Value must be a positive number");
    }

    if (item.quantity === undefined || item.quantity < 1) {
      errors.push("Quantity must be at least 1");
    }

    if (item.equipped && !item.equippedSlot) {
      errors.push("Equipment slot is required when equipping items");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get rarity color for UI display
   */
  static getRarityColor(rarity: InventoryItem["rarity"]): string {
    switch (rarity) {
      case "common":
        return "#9d9d9d";
      case "uncommon":
        return "#1a9850";
      case "rare":
        return "#2b83ba";
      case "very_rare":
        return "#9e59ae";
      case "legendary":
        return "#ffd700";
      default:
        return "#9d9d9d";
    }
  }

  /**
   * Get item type icon or label
   */
  static getItemTypeLabel(type: InventoryItem["type"]): string {
    switch (type) {
      case "weapon":
        return "⚔️ Weapon";
      case "armor":
        return "🛡️ Armor";
      case "magical":
        return "✨ Magical";
      case "consumable":
        return "🧪 Consumable";
      case "item":
        return "📦 Item";
      default:
        return "📦 Item";
    }
  }

  /**
   * Calculate total weight of items
   */
  static calculateTotalWeight(items: InventoryItem[]): number {
    return items.reduce(
      (total, item) => total + item.weight * item.quantity,
      0
    );
  }

  /**
   * Get equipped items by slot
   */
  static getEquippedItemsBySlot(
    items: InventoryItem[]
  ): Record<string, InventoryItem[]> {
    const equippedBySlot: Record<string, InventoryItem[]> = {};

    items.forEach((item) => {
      if (item.equipped && item.equippedSlot) {
        if (!equippedBySlot[item.equippedSlot]) {
          equippedBySlot[item.equippedSlot] = [];
        }
        equippedBySlot[item.equippedSlot].push(item);
      }
    });

    return equippedBySlot;
  }

  /**
   * Get items by type
   */
  static getItemsByType(
    items: InventoryItem[],
    type: InventoryItem["type"]
  ): InventoryItem[] {
    return items.filter((item) => item.type === type);
  }

  /**
   * Get items by rarity
   */
  static getItemsByRarity(
    items: InventoryItem[],
    rarity: InventoryItem["rarity"]
  ): InventoryItem[] {
    return items.filter((item) => item.rarity === rarity);
  }

  /**
   * Get attuned items
   */
  static getAttunedItems(
    items: InventoryItem[],
    characterId: string
  ): InventoryItem[] {
    return items.filter((item) => item.attunedBy === characterId);
  }
}

export default InventoryService;
